/**
 * Error logging
 *
 * Can be used to log errors to console or and eternal
 * error logging system, like Sentry for example.
 *
 */

import * as Sentry from '@sentry/browser';
import appSettings from '../config/settings';

/**
 * Set up error handling. If a Sentry DSN is
 * provided a Sentry client will be installed.
 */
export const setup = () => {
  if (appSettings.sentryDsn) {
    // Configures the Sentry client. Adds a handler for
    // any uncaught exception.
    Sentry.init({
      dsn: appSettings.sentryDsn,
      environment: appSettings.env,
      beforeSend(event) {
        // Sharetribe SDK closes its internal WebSocket without a clean handshake on
        // page unload / tab close / mobile backgrounding — code 1006 is expected and
        // unactionable. Drop these to keep the error dashboard meaningful.
        const msg = event?.message || event?.exception?.values?.[0]?.value || '';
        if (msg.includes('WebSocket') && msg.includes('1006')) return null;
        return event;
      },
    });
  }
};

/**
 * Set user ID for the logger so that it
 * can be attached to Sentry issues.
 *
 * @param {String} userId ID of current user
 */
export const setUserId = userId => {
  Sentry.configureScope(scope => {
    scope.setUser({ id: userId });
  });
};

/**
 * Clears the user ID.
 */

export const clearUserId = () => {
  Sentry.configureScope(scope => {
    scope.setUser(null);
  });
};

/**
 * Adds a breadcrumb to Sentry (no-op if Sentry DSN is not configured).
 *
 * @param {String} category Breadcrumb category, e.g. "ui" or "upload"
 * @param {String} message Breadcrumb message
 * @param {Object} data Additional data (avoid PII)
 */
export const breadcrumb = (category, message, data) => {
  if (!appSettings.sentryDsn) return;
  try {
    Sentry.addBreadcrumb({ category, message, data });
  } catch (e) {
    // Ignore logging failures
  }
};

/**
 * Captures a non-error message to Sentry (no-op if Sentry DSN is not configured).
 *
 * @param {String} msg Message
 * @param {String} level Sentry level: "info" | "warning" | "error" | "debug" | "fatal"
 * @param {Object} data Additional data (avoid PII)
 */
export const message = (msg, level = 'info', data) => {
  if (!appSettings.sentryDsn) return;
  try {
    Sentry.withScope(scope => {
      if (level) scope.setLevel(level);
      if (data && typeof data === 'object') {
        if (data.code) scope.setTag('code', data.code);
        if (data.area) scope.setTag('area', data.area);
        if (data.mode) scope.setTag('mode', data.mode);
      }
      if (data && typeof data === 'object') {
        Object.keys(data).forEach(key => {
          scope.setExtra(key, data[key]);
        });
      }
      Sentry.captureMessage(msg);
    });
  } catch (e) {
    // Ignore logging failures
  }
};

/**
 * Temporarily capture runtime errors that happen right after a user interaction.
 * Useful for isolating mobile-only issues around specific UI actions.
 *
 * Captures:
 * - window.onerror
 * - unhandledrejection
 * - console.error (as Sentry messages, limited)
 *
 * Returns a stop() function.
 *
 * @param {Number} durationMs How long to capture (default 15000ms)
 * @param {Object} context Extra context/tags (avoid PII)
 */
export const captureErrorsFor = (durationMs = 15000, context = {}) => {
  if (!appSettings.sentryDsn) return () => {};
  if (typeof window === 'undefined') return () => {};

  let active = true;
  let consoleErrorCount = 0;
  const maxConsoleErrors = 5;

  const onError = event => {
    if (!active) return;
    try {
      const err =
        event?.error instanceof Error
          ? event.error
          : new Error(event?.message || 'window.onerror');
      error(err, context.code || 'window-onerror', {
        ...context,
        source: 'window.onerror',
        message: event?.message,
        filename: event?.filename,
        lineno: event?.lineno,
        colno: event?.colno,
      });
    } catch (e) {
      // no-op
    }
  };

  const onUnhandledRejection = event => {
    if (!active) return;
    try {
      const reason = event?.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : 'unhandledrejection');
      error(err, context.code || 'unhandledrejection', {
        ...context,
        source: 'unhandledrejection',
        reasonType: typeof reason,
      });
    } catch (e) {
      // no-op
    }
  };

  const originalConsoleError = console.error;
  console.error = (...args) => {
    originalConsoleError(...args);
    if (!active) return;
    if (consoleErrorCount >= maxConsoleErrors) return;
    consoleErrorCount += 1;

    const first = args?.[0];
    if (first instanceof Error) {
      error(first, context.code || 'console.error', { ...context, source: 'console.error' });
      return;
    }
    try {
      const text = args
        .map(a => (typeof a === 'string' ? a : a && a.message ? a.message : JSON.stringify(a)))
        .join(' ')
        .slice(0, 500);
      message('console.error', 'error', { ...context, source: 'console.error', text });
    } catch (e) {
      // no-op
    }
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  const stop = () => {
    if (!active) return;
    active = false;
    try {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    } catch (e) {
      // no-op
    }
    try {
      console.error = originalConsoleError;
    } catch (e) {
      // no-op
    }
  };

  const timeoutId = window.setTimeout(stop, durationMs);
  // Ensure stop clears timeout if called early
  return () => {
    try {
      window.clearTimeout(timeoutId);
    } catch (e) {
      // no-op
    }
    stop();
  };
};

const printAPIErrorsAsConsoleTable = apiErrors => {
  if (apiErrors != null && apiErrors.length > 0 && typeof console.table === 'function') {
    console.log('Errors returned by Marketplace API call:');
    console.table(apiErrors.map(err => ({ status: err.status, code: err.code, ...err.meta })));
  }
};

const responseAPIErrors = error => {
  return error && error.data && error.data.errors ? error.data.errors : [];
};

const responseApiErrorInfo = err =>
  responseAPIErrors(err).map(e => ({
    status: e.status,
    code: e.code,
    meta: e.meta,
  }));

/**
 * Logs an exception. If Sentry is configured
 * sends the error information there. Otherwise
 * prints the error to the console.
 *
 * @param {Error} e Error that occurred
 * @param {String} code Error code
 * @param {Object} data Additional data to be sent to Sentry
 */
export const error = (e, code, data) => {
  const apiErrors = responseApiErrorInfo(e);
  if (appSettings.sentryDsn) {
    const extra = { ...data, apiErrorData: apiErrors };

    Sentry.withScope(scope => {
      scope.setTag('code', code);
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(e);
    });

    printAPIErrorsAsConsoleTable(apiErrors);
  } else {
    console.error(e);
    console.error('Error code:', code, 'data:', data);
    printAPIErrorsAsConsoleTable(apiErrors);
  }
};
